import { useRef, useState } from "react";
import {
  IconArrowUpDown,
  IconChevronDown,
  IconChevronUp,
} from "@/components/icons";
import { useClickOutside } from "@/hooks/useClickOutside";
import {
  FORMS_LIST_SORT_OPTIONS,
  parseSortOptionValue,
  toSortOptionValue,
  type FormsListSortBy,
  type FormsListSortDir,
} from "@/lib/admin/formsListPreferences";

interface FormsListSortHeaderProps {
  sortBy: FormsListSortBy;
  sortDir: FormsListSortDir;
  onSortChange: (sortBy: FormsListSortBy, sortDir: FormsListSortDir) => void;
}

function SortIndicator({
  sortBy,
  sortDir,
}: {
  sortBy: FormsListSortBy;
  sortDir: FormsListSortDir;
}) {
  if (sortBy === "createdAt") {
    return sortDir === "desc" ? (
      <IconChevronDown size={14} className="shrink-0 text-brand-600" />
    ) : (
      <IconChevronUp size={14} className="shrink-0 text-brand-600" />
    );
  }

  return <IconArrowUpDown size={14} className="shrink-0 text-slate-400" />;
}

export function FormsListSortHeader({
  sortBy,
  sortDir,
  onSortChange,
}: FormsListSortHeaderProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLTableCellElement>(null);
  const currentValue = toSortOptionValue(sortBy, sortDir);

  useClickOutside(containerRef, () => setOpen(false), open);

  return (
    <th
      ref={containerRef}
      className="relative w-24 whitespace-nowrap px-2 py-1.5 text-left font-onest text-[11px] font-medium leading-4 text-slate-400"
    >
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="listbox"
        title="Сортировка"
        onClick={() => setOpen((value) => !value)}
        className="inline-flex items-center gap-1 rounded-md py-0.5 font-onest text-[11px] font-medium leading-4 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
      >
        Дата
        <SortIndicator sortBy={sortBy} sortDir={sortDir} />
      </button>

      {open ? (
        <div
          className="absolute right-0 top-full z-30 mt-1 min-w-[12.5rem] rounded-lg border border-slate-200/90 bg-white py-1 shadow-lg"
          role="listbox"
          aria-label="Сортировка"
        >
          {FORMS_LIST_SORT_OPTIONS.map((option) => {
            const selected = option.value === currentValue;
            return (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={selected}
                onClick={() => {
                  const parsed = parseSortOptionValue(option.value);
                  if (parsed) {
                    onSortChange(parsed.sortBy, parsed.sortDir);
                  }
                  setOpen(false);
                }}
                className={[
                  "flex w-full px-3 py-1.5 text-left font-onest text-sm transition-colors",
                  selected
                    ? "bg-brand-50 font-medium text-brand-800"
                    : "text-slate-700 hover:bg-slate-50",
                ].join(" ")}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      ) : null}
    </th>
  );
}
